import { useEffect, useState, useCallback } from 'react';

const isStandaloneDisplay = () => {
    try {
        return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    } catch (_) {
        return false;
    }
};

const isIosDevice = () => {
    try {
        return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
    } catch (_) {
        return false;
    }
};

// Wraps the native `beforeinstallprompt` event (Chrome/Edge/Android only — iOS Safari
// never fires it) so a custom in-app button can trigger the install flow. The browser's
// own address-bar install affordance is easy to miss, so we capture and hold onto the
// event here and let UI re-fire it on demand via promptInstall().
export function useInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [isInstalled, setIsInstalled] = useState(isStandaloneDisplay);

    useEffect(() => {
        const onBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setIsInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.addEventListener('appinstalled', onInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
            window.removeEventListener('appinstalled', onInstalled);
        };
    }, []);

    const promptInstall = useCallback(async () => {
        if (!deferredPrompt) return null;
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        setDeferredPrompt(null);
        return choice; // { outcome: 'accepted' | 'dismissed', platform }
    }, [deferredPrompt]);

    return {
        canInstall: !!deferredPrompt && !isInstalled,
        isInstalled,
        isIos: isIosDevice(),
        promptInstall,
    };
}
